import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Save, CheckCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComprehensiveQuestionnaire = ({ user, onLogout }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [responses, setResponses] = useState({});
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const sections = [
    {
      title: 'Personal & Financial Background',
      questions: [
        {
          id: 'age',
          question: 'Current age (years):',
          type: 'radio',
          options: [
            { value: '<40', label: 'Less than 40 years' },
            { value: '41-50', label: '41 to 50 years' },
            { value: '51-65', label: '51 to 65 years' },
            { value: '>65', label: 'More than 65 years' }
          ]
        },
        {
          id: 'net_worth',
          question: 'Net worth (INR crores):',
          type: 'radio',
          options: [
            { value: '<50', label: 'Less than ₹50 Crores' },
            { value: '50-100', label: '₹50 to ₹100 Crores' },
            { value: '100-500', label: '₹100 to ₹500 Crores' },
            { value: '>500', label: 'More than ₹500 Crores' }
          ]
        },
        {
          id: 'investments_excluding_real_estate',
          question: 'Investments excluding residential real estate (INR crores):',
          type: 'radio',
          options: [
            { value: '<25', label: 'Less than ₹25 Crores' },
            { value: '25-100', label: '₹25 to ₹100 Crores' },
            { value: '100-500', label: '₹100 to ₹500 Crores' },
            { value: '>500', label: 'More than ₹500 Crores' }
          ]
        },
        {
          id: 'income_generating_assets',
          question: 'Assets in income generating investments (deposits, bonds, debt mutual funds, rental etc.):',
          type: 'radio',
          options: [
            { value: '<25%', label: 'Less than 25%' },
            { value: '25-50%', label: '25% to 50%' },
            { value: '50-75%', label: '50% to 75%' },
            { value: '>75%', label: 'More than 75%' }
          ]
        }
      ]
    },
    {
      title: 'Investment Knowledge & Experience',
      questions: [
        {
          id: 'investment_familiarity',
          question: 'How familiar are you with the world of investments?',
          type: 'radio',
          options: [
            { value: 'not_aware', label: 'I am not aware of the recent developments' },
            { value: 'aware_not_applied', label: 'I know what\'s trending in the financial markets, but I cannot use it to manage my investments portfolio' },
            { value: 'adequate_knowledge', label: 'I have adequate information on financial markets and its impact on my portfolio' }
          ]
        },
        {
          id: 'portfolio_management_experience',
          question: 'How many years of experience do you have in managing your investment portfolio?',
          type: 'radio',
          options: [
            { value: '<10', label: 'Less than 10 years' },
            { value: '10-20', label: '10 to 20 years' },
            { value: '>20', label: 'More than 20 years' }
          ]
        },
        {
          id: 'recreation_preference',
          question: 'As an individual, which recreation or sport do/did you most relate with?',
          type: 'radio',
          options: [
            { value: 'high_risk', label: 'Mountaineering, skydiving, car racing' },
            { value: 'low_risk', label: 'Golf, swimming, archery' },
            { value: 'moderate_risk', label: 'Football, cycling, running' }
          ]
        }
      ]
    },
    {
      title: 'Financial Stability & Goals',
      questions: [
        {
          id: 'income_stability',
          question: 'How stable is your current (and future) source of income such as business income, salary, fixed returns assets, dividends etc.?',
          type: 'radio',
          options: [
            { value: 'unpredictable', label: 'Unpredictable' },
            { value: 'somewhat_stable', label: 'Somewhat stable' },
            { value: 'very_stable', label: 'Very stable' }
          ]
        },
        {
          id: 'investment_seeking',
          question: 'What are you seeking from your investments?',
          type: 'radio',
          options: [
            { value: 'mostly_income', label: 'Mostly income' },
            { value: 'blend', label: 'Blend of income and capital appreciation' },
            { value: 'mostly_appreciation', label: 'Mostly capital appreciation' }
          ]
        },
        {
          id: 'liquidity_needs',
          question: 'Do you need to draw from your investment portfolio to meet expenses in the next 3 years?',
          type: 'radio',
          options: [
            { value: 'no', label: 'No' },
            { value: 'up_to_10', label: 'Yes, up to 10%' },
            { value: 'up_to_25', label: 'Yes, up to 25%' }
          ]
        },
        {
          id: 'investment_horizon',
          question: 'What is your concept of long-term with respect to your portfolio?',
          type: 'radio',
          options: [
            { value: '1-3', label: '1 to 3 years' },
            { value: '3-10', label: '3 to 10 years' },
            { value: '>10', label: 'More than 10 years' }
          ]
        }
      ]
    },
    {
      title: 'Risk Understanding & Tolerance',
      questions: [
        {
          id: 'fixed_income_understanding',
          question: 'Can investments in fixed income-oriented products return negative in a year?',
          type: 'radio',
          options: [
            { value: 'no_stable', label: 'No, they are fixed income and quite stable' },
            { value: 'yes_interest_rates', label: 'Yes, if interest rates rise significantly' }
          ]
        },
        {
          id: 'market_correction_behavior',
          question: 'In the last 15 years, there have been multiple times that Indian equities have corrected by more than 25%. If you see or are advised on a major correction coming, you would...',
          type: 'radio',
          options: [
            { value: 'sell_all', label: 'Sell all equity investments' },
            { value: 'sell_some', label: 'Sell some of the equity investments' },
            { value: 'hold', label: 'Hold on' },
            { value: 'buy_more', label: 'Buy more if there\'s available surplus' }
          ]
        },
        {
          id: 'underperformance_patience',
          question: 'Investment products can have a poor run with negative returns and/or lag the benchmark. Upon such observation, how long would you hold the product before deciding to sell?',
          type: 'radio',
          options: [
            { value: '<1', label: 'Less than 1 year' },
            { value: '1-3', label: '1 to 3 years' },
            { value: '>3', label: 'More than 3 years' }
          ]
        },
        {
          id: 'safety_vs_returns',
          question: 'Between higher degree of safety and higher returns, would you choose safety even if you were told that the returns may not beat inflation?',
          type: 'radio',
          options: [
            { value: 'safety_paramount', label: 'Yes, safety is paramount' },
            { value: 'trade_some_safety', label: 'I can trade some amount of safety to ensure my investments beat inflation' },
            { value: 'seek_higher_returns', label: 'No, I will seek higher returns if \'safe\' products do not even beat inflation' }
          ]
        },
        {
          id: 'short_term_loss_tolerance',
          question: 'Would you tolerate capital loss in the short-term (say 1-2 years) if there is potential of a strong upside over the longer term?',
          type: 'radio',
          options: [
            { value: 'no', label: 'No' },
            { value: 'depends', label: 'Cannot say, depends on the quantum of loss' },
            { value: 'yes', label: 'Yes' }
          ]
        }
      ]
    }
  ];

  useEffect(() => {
    fetchExistingQuestionnaire();
  }, []);

  const fetchExistingQuestionnaire = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/questionnaire/me`, config);
      
      if (response.data.completed !== false) {
        setExisting(response.data);
        setResponses(response.data.responses || {});
      }
    } catch (error) {
      console.error('Error fetching questionnaire:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      console.log('Submitting questionnaire:', responses);
      const result = await axios.post(`${API}/questionnaire`, { responses }, config);
      console.log('Questionnaire submitted successfully:', result.data);
      
      setSuccess(true);
      
      // Wait a bit longer for backend to process and save
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Navigate to results page
      navigate('/risk-profile-results');
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      alert('Failed to submit questionnaire: ' + (error.response?.data?.detail || error.message));
      setSubmitting(false);
    }
  };

  const isCurrentSectionComplete = () => {
    const currentQuestions = sections[currentSection].questions;
    return currentQuestions.every(q => responses[q.id]);
  };

  const getTotalProgress = () => {
    const totalQuestions = sections.reduce((acc, section) => acc + section.questions.length, 0);
    const answeredQuestions = Object.keys(responses).length;
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ugmonk-yellow"></div>
        </div>
      </Layout>
    );
  }

  if (success) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center h-96 space-y-4">
          <CheckCircle size={64} className="text-ugmonk-grey" />
          <h2 className="text-2xl font-bold text-gray-900">Questionnaire Completed!</h2>
          <p className="text-gray-600">Thank you for completing your investment profile.</p>
          <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
            Investment Profile Assessment
          </h1>
          <p className="text-gray-600">
            {existing ? 'Update your investment profile and risk assessment' : 'Complete this comprehensive assessment to help us understand your investment goals and risk tolerance'}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-sm font-semibold text-ugmonk-dark">{getTotalProgress()}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-ugmonk-yellow to-ugmonk-yellow h-2 rounded-full transition-all duration-300"
              style={{ width: `${getTotalProgress()}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-3">
            {sections.map((section, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSection(idx)}
                className={`text-xs px-2 py-1 rounded ${
                  idx === currentSection 
                    ? 'bg-[#fffdf0] text-ugmonk-dark font-semibold' 
                    : idx < currentSection
                    ? 'text-ugmonk-dark'
                    : 'text-gray-500'
                }`}
              >
                Section {idx + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Current Section */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#fffdf0] text-ugmonk-dark text-sm font-bold">
              {currentSection + 1}
            </span>
            {sections[currentSection].title}
          </h2>

          <div className="space-y-8">
            {sections[currentSection].questions.map((q, index) => (
              <div key={q.id} className="space-y-3">
                <label className="block text-gray-900 font-medium">
                  {index + 1}. {q.question}
                </label>
                
                <div className="space-y-2 ml-4">
                  {q.options.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        responses[q.id] === opt.value
                          ? 'border-ugmonk-yellow bg-[#fffdf0]'
                          : 'border-gray-200 hover:border-ugmonk-border hover:bg-[#fffdf0]/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        value={opt.value}
                        checked={responses[q.id] === opt.value}
                        onChange={(e) => handleInputChange(q.id, e.target.value)}
                        className="mt-1 text-ugmonk-grey focus:ring-ugmonk-yellow"
                      />
                      <span className="text-gray-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between gap-4">
          <button
            onClick={handlePrevious}
            disabled={currentSection === 0}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              currentSection === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-ugmonk-yellow hover:text-ugmonk-dark'
            }`}
          >
            <ChevronLeft size={20} />
            Previous
          </button>

          {currentSection < sections.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={!isCurrentSectionComplete()}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                isCurrentSectionComplete()
                  ? 'bg-gradient-to-r from-ugmonk-yellow to-[#f5cc6a] hover:from-[#f5cc6a] hover:to-ugmonk-yellow text-ugmonk-dark'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next
              <ChevronRight size={20} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || !isCurrentSectionComplete()}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                isCurrentSectionComplete() && !submitting
                  ? 'bg-gradient-to-r from-ugmonk-yellow to-[#f5cc6a] hover:from-[#f5cc6a] hover:to-ugmonk-yellow text-ugmonk-dark'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Save size={20} />
              {submitting ? 'Submitting...' : existing ? 'Update Profile' : 'Complete Assessment'}
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ComprehensiveQuestionnaire;
